import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useNotificationCapabilities } from '@/core/hooks/use-notification-capabilities';
import { userPrefsKey } from '@/core/hooks/use-notification-preferences';
import { getPushPermissionStatus } from '@/core/lib/push-notifications';
import { syncPushGrantIfPermitted } from '@/core/lib/push-permission-flow';
import { AuthStatus, useAuthStore } from '@/core/store/auth.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

/** Silently register token + apply push prefs when OS permission is already granted. */
export function usePushPermissionSync() {
  const queryClient = useQueryClient();
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const authStatus = useAuthStore((state) => state.authStatus);
  const { data: capabilities } = useNotificationCapabilities();
  const syncedWorkspaceRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      !isCapacitorNative() ||
      authStatus !== AuthStatus.AUTHENTICATED ||
      !capabilities?.pushEnabled ||
      !workspaceId ||
      syncedWorkspaceRef.current === workspaceId
    ) {
      return;
    }

    let cancelled = false;

    void (async () => {
      const status = await getPushPermissionStatus();
      if (cancelled || status !== 'granted') return;

      syncedWorkspaceRef.current = workspaceId;
      await syncPushGrantIfPermitted(workspaceId);
      void queryClient.invalidateQueries({
        queryKey: userPrefsKey(workspaceId),
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [authStatus, capabilities?.pushEnabled, queryClient, workspaceId]);
}
